// Which Reporting tabs a company gets, and whether it gets the page at all.
//
// Kept out of the page component so both rules are directly testable - jest cannot resolve the
// `src/`-aliased imports the page pulls in.

/**
 * Tabs by customer type, in display order. The first entry is the default tab.
 *
 * AppFolio customers bill through AppFolio, so Accounts Receivable and Reimbursement Receipts -
 * both of which report on money Livable collected and pays back - do not apply. Utility Allocation
 * Export does: it reports on the utility bills and their allocation, which happens here either way.
 *
 * The signal is `isAppfolio` from company settings, NOT `propertyManagementSoftware`. That field is
 * free text captured at onboarding and does not track the integration: in prod, 72 companies have
 * it set to "AppFolio" while only 16 carry the flag, and 88 companies that DO carry the flag have
 * it empty. Gating on the text would get both groups wrong.
 *
 * A company with no CompanySettings row (about 70% of them) has every flag default to false and is
 * treated as a regular customer - correct, since no settings row means no AppFolio integration.
 */
/**
 * Whether this is an AppFolio customer, read from BOTH stores that carry the flag.
 *
 * They hydrate independently and each defaults the flag to false, so reading only
 * `companyFeatureStore` means an AppFolio customer looks like a regular one until it lands. That
 * window is not harmless: `disableReports` is read from `homeDashboardStore` as well, and every
 * AppFolio company has it set, so a dashboard-first hydration produced "reports disabled, not
 * AppFolio" and redirected them off the page. `observer` cannot undo that - the redirect has
 * already replaced the route by the time the flag arrives.
 */
export const isAppfolioFrom = (companyFlag, dashboardFlag) => companyFlag === true || dashboardFlag === true;

/**
 * Convergent customers get the same single tab: their property management software bills and collects, so
 * Accounts Receivable and Reimbursement Receipts report on money Livable never handled (prod 2026-10-08: zero
 * resident payments, reimbursements or open AR across every convergent company in six months).
 */
export const tabsFor = (isAppfolio, convergent) => isAppfolio || convergent ? ['utilityAllocationExport'] : ['reimbursementV2', 'reimbursementCustom', 'utilityAllocationExport'];

/**
 * Whether the Reporting page is off for this company.
 *
 * Two things are going on here.
 *
 * 1. Reports are off when EITHER source says so. This was an OR of two negations, which only hid
 *    the page when both sources agreed and therefore ignored a single source saying "disabled".
 *    The same expression was also used by PmSidebar to SHOW the link, so the page and the sidebar
 *    disagreed about what it meant.
 *
 * 2. `disableReports` does not apply to AppFolio customers. In prod it is a perfect proxy for
 *    `isAppfolio` - all 125 AppFolio companies have it set, no exceptions - because the whole
 *    Reporting page was irrelevant to them when the flag was introduced. Utility Allocation Export
 *    changes that: it reports on utility bills and their allocation, which happens in Livable for
 *    AppFolio customers too. Honouring the flag for them makes their one applicable tab
 *    unreachable, which is exactly what it does today.
 *
 *    This has to be handled here rather than in the data. The flag has no admin UI in either repo;
 *    its only writer is IntegrationService.HandleCompanyIntegrationMessage, which sets it from an
 *    upstream Kafka message on every company sync. Clearing it on those 125 rows would therefore be
 *    reverted by the next sync, and there is no per-company override to lose - that capability
 *    never existed.
 */
export const reportsDisabledFor = (companyFlag, dashboardFlag, isAppfolio) => {
  if (isAppfolio) return false;
  return companyFlag === true || dashboardFlag === true;
};